import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MyButton } from '../index';
import {
  deleteConfirmModalStyles,
  getDeleteConfirmModalThemeStyles,
} from './DeleteConfirmModal.styles';

const DeleteConfirmModal = ({
  open = false,
  onClose,
  onConfirm,
  title,
  message,
  itemName = '',
  entityType = 'record',
  confirmText,
  cancelText,
  loading = false,
  icon,
  ...props
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  const handleClose = () => {
    if (!loading && onClose) {
      onClose();
    }
  };
  const modalTitle = title || t('modals.delete.title');
  const modalMessage = message || t(`modals.delete.messages.${entityType}`);
  const modalConfirmText = confirmText || t('modals.delete.confirm');
  const modalCancelText = cancelText || t('modals.delete.cancel');
  const modalIcon = icon || <WarningIcon sx={deleteConfirmModalStyles.warningIcon} />;
  const themeAwareStyles = getDeleteConfirmModalThemeStyles(theme);

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose(e, reason);
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      sx={themeAwareStyles.dialog}
      {...props}
    >
      <DialogTitle sx={themeAwareStyles.dialogTitle}>
        <Box sx={deleteConfirmModalStyles.titleContainer}>
          <Box sx={deleteConfirmModalStyles.titleContent}>
            {modalIcon}
            <Typography variant="h6" sx={deleteConfirmModalStyles.titleText}>
              {modalTitle}
            </Typography>
          </Box>

          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={deleteConfirmModalStyles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={deleteConfirmModalStyles.dialogContent}>
        <Typography variant="body1" sx={deleteConfirmModalStyles.messageText}>
          {modalMessage}
        </Typography>
        {itemName && (
          <Box sx={themeAwareStyles.itemNameContainer}>
            <Typography variant="body1" sx={deleteConfirmModalStyles.itemName}>
              "{itemName}"
            </Typography>
          </Box>
        )}
        <Box sx={deleteConfirmModalStyles.warningNote}>
          <Typography variant="caption" color="text.secondary">
            {t('modals.delete.warning')}.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={deleteConfirmModalStyles.dialogActions}>
        <MyButton
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={themeAwareStyles.cancelButton}
        >
          {modalCancelText}
        </MyButton>

        <MyButton
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={loading}
          sx={deleteConfirmModalStyles.confirmButton}
        >
          {loading ? t('modals.delete.deleting') : modalConfirmText}
        </MyButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmModal;
